import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import Select from 'react-select';
import api from '../services/api';
import Swal from 'sweetalert2';
import { useAuth } from '../contexts/AuthContext';

const AbsenceModal = ({ isOpen, onClose, inscriptionId, inscriptionData }) => {
  const { user } = useAuth();
  const [periodes, setPeriodes] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    inscription_id: inscriptionId || '',
    periode_id: '',
    date_absence: new Date().toISOString().slice(0, 10),
    journee_entiere: 'N',
    incident_id: '',
    motif: '',
    dmd_constat: 'Constaté',
    user_id: user?.id || ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchPeriodes();
      fetchIncidents();
      if (inscriptionId) {
        setForm(prev => ({ ...prev, inscription_id: inscriptionId }));
      } else {
        setForm(prev => ({ ...prev, inscription_id: '' }));
      }
    }
  }, [isOpen, inscriptionId]);

  const fetchPeriodes = async () => {
    try {
      const res = await api.get('/periodes');
      setPeriodes(res.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des périodes:', error);
    }
  };

  const fetchIncidents = async () => {
    try {
      const res = await api.get('/incidents');
      setIncidents(res.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des incidents:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === 'checkbox' ? (checked ? 'Y' : 'N') : value
    });
  };

  const handleSelect = (name, option) => {
    setForm({
      ...form,
      [name]: option ? option.value : ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.inscription_id || !form.periode_id || !form.date_absence) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: 'Les champs obligatoires doivent être remplis.' });
      return;
    }

    setLoading(true);
    try {
      const dataToSend = { ...form, user_id: user?.id };
      await api.post('/absences', dataToSend);
      Swal.fire('Succès', 'Absence ajoutée avec succès', 'success');
      onClose();
      // Réinitialiser le formulaire
      setForm({
        inscription_id: inscriptionId || '',
        periode_id: '',
        date_absence: new Date().toISOString().slice(0, 10),
        journee_entiere: 'N',
        incident_id: '',
        motif: '',
        dmd_constat: 'Constaté',
        user_id: user?.id || ''
      });
    } catch (error) {
      Swal.fire('Erreur', 'Impossible d\'ajouter l\'absence', 'error');
    } finally {
      setLoading(false);
    }
  };

  const periodesOptions = periodes.map(p => ({
    value: p.id,
    label: p.periode || `Période ${p.id}`
  }));

  const incidentsOptions = incidents.map(i => ({
    value: i.id,
    label: i.designation || `Incident ${i.id}`
  }));

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Ajouter une absence"
      ariaHideApp={false}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          zIndex: 1000
        },
        content: {
          maxWidth: '600px',
          width: '100%',
          margin: 'auto',
          borderRadius: 12,
          padding: 0
        }
      }}
    >
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            Ajouter une absence
            {inscriptionData && (
              <span className="text-sm font-normal text-gray-600 ml-2">
                - {inscriptionData.nom} {inscriptionData.prenom}
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
            title="Fermer"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!inscriptionId && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Élève *</label>
                <Select
                  options={[]}
                  value={null}
                  onChange={opt => handleSelect('inscription_id', opt)}
                  isClearable
                  isSearchable
                  placeholder="Rechercher un élève..."
                  classNamePrefix="react-select"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Cette fonctionnalité nécessite de sélectionner un élève depuis la page Absences
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Période *</label>
              <Select
                options={periodesOptions}
                value={periodesOptions.find(o => o.value === form.periode_id) || null}
                onChange={opt => handleSelect('periode_id', opt)}
                isClearable
                placeholder="Sélectionner une période"
                classNamePrefix="react-select"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Date *</label>
              <input
                type="date"
                name="date_absence"
                value={form.date_absence}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="journee_entiere"
                name="journee_entiere"
                checked={form.journee_entiere === 'Y'}
                onChange={handleChange}
                className="mr-2"
              />
              <label htmlFor="journee_entiere" className="text-sm font-medium">Journée entière</label>
            </div>
            <div className="relative">
              <label className="block text-sm font-medium mb-1">Incident associé</label>
              <div className="flex gap-2">
                <Select
                  options={incidentsOptions}
                  value={incidentsOptions.find(o => o.value === form.incident_id) || null}
                  onChange={opt => handleSelect('incident_id', opt)}
                  isClearable
                  placeholder="Aucun"
                  classNamePrefix="react-select"
                  className="flex-1"
                />
                <button
                  type="button"
                  onClick={async () => {
                    // Ouvrir un modal rapide pour créer un incident
                    const { value: formValues } = await Swal.fire({
                      title: 'Nouvel incident',
                      html: `
                        <input id="swal-designation" class="swal2-input" placeholder="Désignation *" required>
                        <input id="swal-retrait" type="number" step="0.01" class="swal2-input" placeholder="Retrait de points (optionnel)">
                      `,
                      focusConfirm: false,
                      showCancelButton: true,
                      confirmButtonText: 'Créer',
                      cancelButtonText: 'Annuler',
                      preConfirm: () => {
                        const designation = document.getElementById('swal-designation').value;
                        const retrait = document.getElementById('swal-retrait').value;
                        if (!designation) {
                          Swal.showValidationMessage('La désignation est obligatoire');
                          return false;
                        }
                        return { designation, retrait: retrait || null };
                      }
                    });

                    if (formValues) {
                      try {
                        const res = await api.post('/incidents', formValues);
                        await fetchIncidents();
                        setForm({ ...form, incident_id: res.data.data.id });
                        Swal.fire('Succès', 'Incident créé avec succès', 'success');
                      } catch (error) {
                        Swal.fire('Erreur', 'Impossible de créer l\'incident', 'error');
                      }
                    }
                  }}
                  className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300 text-gray-700"
                  title="Ajouter un incident"
                >
                  +
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Motif</label>
              <input
                type="text"
                name="motif"
                value={form.motif}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Constat</label>
              <input
                type="text"
                name="dmd_constat"
                value={form.dmd_constat}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Ajout...' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AbsenceModal;

